import { DragHandleIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Center,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverFooter,
  Switch,
  Text,
  HStack,
  Tooltip,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from '@hello-pangea/dnd';
import React, { useEffect, useState } from 'react';

import { finleyAssert } from '@finley-technologies/common';

import { Icon } from '~/packages/common-frontend/components/icons';
import { ColorScheme } from '~/packages/common-frontend/theme';

export type ColumnConfiguration<T extends string = string> = {
  columnKey: T;
  isVisible: boolean;
  isFixed: boolean;
};

interface ColumnOptionProps {
  isFixed: boolean;
  isVisible: boolean;
  columnKey: string;
  columnName: string;

  updateVisibility: (columnKey: string) => void;
  isDragging: boolean;
}

export const ColumnOption = ({
  columnName,
  isFixed,
  isVisible,
  isDragging,
  columnKey,

  updateVisibility,
}: ColumnOptionProps) => {
  const draggingColor = useColorModeValue('gray.100', 'gray.800');
  const bgColor = useColorModeValue('white', 'gray.900');
  const borderColor = useColorModeValue('gray.200', 'gray.800');
  return (
    <HStack
      sx={{
        w: '100%',
        p: '16px 8px',
        bgColor: isDragging ? draggingColor : bgColor,
        alignItems: 'center',
        border: `1px solid`,
        borderColor,
        borderRadius: '0.5rem',
      }}
    >
      <HStack sx={{ spacing: '8px', flexGrow: 1 }}>
        {!isFixed && <DragHandleIcon />}
        {isFixed && (
          <Tooltip label="This column is fixed" hasArrow>
            <Center>
              <Icon icon="lock" />
            </Center>
          </Tooltip>
        )}
        <HStack sx={{ spacing: '8px' }}>
          <Text sx={{ justifySelf: 'flex-start' }}>{columnName}</Text>
        </HStack>
      </HStack>
      <Switch
        isChecked={isVisible}
        onChange={() => {
          updateVisibility(columnKey);
        }}
        justifySelf="flex-end"
        isDisabled={isFixed}
      />
    </HStack>
  );
};

// @jendevelops TODO: update any type to TableConfiguration[columns] type
type Props = {
  initialColumns: ColumnConfiguration[];
  updateColumnConfigs: (columnConfigs: any[]) => void;
  columnNames: Record<string, string>;
  colorScheme?: ColorScheme;
};

const reorder = <TList extends unknown[]>(
  list: TList,
  startIndex: number,
  endIndex: number,
): TList => {
  const result = Array.from(list) as TList;
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

const COLUMN_OPTIONS_PADDING = '4px 0';

export const ColumnDisplaySettingsButton = ({
  initialColumns,
  updateColumnConfigs,
  columnNames,
  colorScheme = 'gray',
}: Props) => {
  const { onClose, isOpen, onOpen } = useDisclosure();
  const [columns, setColumns] = useState(initialColumns);

  useEffect(() => {
    setColumns(initialColumns);
  }, [initialColumns]);

  function onDragEnd(result: DropResult) {
    // dropped outside the list
    if (!result.destination) {
      return;
    }

    if (result.destination.index === result.source.index) {
      return;
    }

    setColumns((previousColumns) => {
      if (!result.destination) {
        return previousColumns;
      }

      const destinationColumn = previousColumns[result.destination.index];

      if (destinationColumn.isFixed) {
        const fixedColumns = previousColumns.filter((column) => column.isFixed);
        const finalFixed = fixedColumns[fixedColumns.length - 1];

        return reorder(
          previousColumns,
          result.source.index,
          previousColumns.indexOf(finalFixed) + 1,
        );
      }

      return reorder(
        previousColumns,
        result.source.index,
        result.destination.index,
      );
    });
  }

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Popover
        placement="bottom-end"
        isOpen={isOpen}
        onOpen={onOpen}
        onClose={onClose}
      >
        <PopoverTrigger>
          <Button variant="secondary-muted" colorScheme={colorScheme}>
            Show/Hide Columns
          </Button>
        </PopoverTrigger>

        <PopoverContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            spacing: '32px',
            w: '400px',
            alignItems: 'flex-start',
          }}
        >
          <Droppable
            droppableId="columnOrdering"
            renderClone={(provided, snapshot, rubric) => {
              const column = columns.find(
                ({ columnKey }) => columnKey === rubric.draggableId,
              );
              finleyAssert(
                column,
                `Expected columnKey [${
                  rubric.draggableId
                }] to exist in columns [${columns
                  .map(({ columnKey }) => columnKey)
                  .join(', ')}]`,
              );
              return (
                <Box
                  ref={provided.innerRef}
                  {...provided.draggableProps}
                  {...provided.dragHandleProps}
                  sx={{ w: '100%', p: COLUMN_OPTIONS_PADDING }}
                >
                  <ColumnOption
                    isDragging={snapshot.isDragging}
                    columnName={
                      columnNames[column.columnKey] ?? column.columnKey
                    }
                    isFixed={column.isFixed}
                    isVisible={column.isVisible}
                    columnKey={column.columnKey}
                    updateVisibility={(columnKey) => {
                      const newColumns = columns.map((col) => {
                        if (col.columnKey === columnKey) {
                          return {
                            ...col,
                            isVisible: !col.isVisible,
                          };
                        }
                        return col;
                      });
                      setColumns(newColumns);
                    }}
                  />
                </Box>
              );
            }}
          >
            {(provided) => (
              <PopoverBody
                ref={provided.innerRef}
                {...provided.droppableProps}
                sx={{
                  w: '100%',
                  p: '1rem',
                  overflowX: 'auto',
                  maxHeight: '400px',
                }}
              >
                {columns.map(({ columnKey, isFixed, isVisible }, index) =>
                  isFixed ? (
                    <Box
                      key={columnKey}
                      sx={{ w: '100%', p: COLUMN_OPTIONS_PADDING }}
                    >
                      <ColumnOption
                        isDragging={false}
                        columnName={columnNames[columnKey] ?? columnKey}
                        isFixed={isFixed}
                        isVisible={isVisible}
                        columnKey={columnKey}
                        updateVisibility={(currColumnKey) => {
                          const newColumns = columns.map((col) => {
                            if (col.columnKey === currColumnKey) {
                              return {
                                ...col,
                                isVisible: !col.isVisible,
                              };
                            }
                            return col;
                          });
                          setColumns(newColumns);
                        }}
                      />
                    </Box>
                  ) : (
                    <Draggable
                      key={columnKey}
                      draggableId={columnKey}
                      index={index}
                    >
                      {(draggableProvided, draggableSnapshot) => {
                        return (
                          <Box
                            ref={draggableProvided.innerRef}
                            {...draggableProvided.draggableProps}
                            {...draggableProvided.dragHandleProps}
                            sx={{ w: '100%', p: COLUMN_OPTIONS_PADDING }}
                          >
                            <ColumnOption
                              isDragging={draggableSnapshot.isDragging}
                              columnName={columnNames[columnKey] ?? columnKey}
                              isFixed={isFixed}
                              isVisible={isVisible}
                              columnKey={columnKey}
                              updateVisibility={(currColumnKey) => {
                                const newColumns = columns.map((col) => {
                                  if (col.columnKey === currColumnKey) {
                                    return {
                                      ...col,
                                      isVisible: !col.isVisible,
                                    };
                                  }
                                  return col;
                                });
                                setColumns(newColumns);
                              }}
                            />
                          </Box>
                        );
                      }}
                    </Draggable>
                  ),
                )}
                {provided.placeholder}
              </PopoverBody>
            )}
          </Droppable>
          <PopoverFooter
            sx={{
              display: 'flex',
              flexDirection: 'row',
              borderTop: 'none',
              gap: '32px',
              w: '100%',
              p: '1rem',
            }}
          >
            <Button
              variant="secondary"
              colorScheme="v2-aurora"
              onClick={() => {
                setColumns(initialColumns);
              }}
              sx={{
                flexGrow: 1,
              }}
            >
              Reset
            </Button>
            <Button
              colorScheme="v2-aurora"
              variant="solid"
              onClick={() => {
                updateColumnConfigs(
                  columns.map(({ columnKey, isFixed, isVisible }) => ({
                    columnKey,
                    isFixed,
                    isVisible,
                  })),
                );
                onClose();
              }}
              sx={{
                flexGrow: 1,
              }}
            >
              Apply
            </Button>
          </PopoverFooter>
        </PopoverContent>
      </Popover>
    </DragDropContext>
  );
};
